import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { useCMS } from '../../context/CMSContext';
import { 
  Users, Plus, Edit, Trash2, Shield, ShieldCheck, 
  Eye, EyeOff, Save, X, AlertCircle, CheckCircle,
  Mail, User, Lock
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminUsersPage = () => {
  const { token, isSuperAdmin, adminUser } = useCMS();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [permissionsTemplate, setPermissionsTemplate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'sub_admin',
    permissions: {}
  });

  // Check if current user is super admin
  useEffect(() => {
    if (!isSuperAdmin()) {
      toast.error('Access denied. Super Admin only.');
    }
  }, [isSuperAdmin]);

  // Fetch users and permissions template
  useEffect(() => {
    fetchUsers();
    fetchPermissionsTemplate();
  }, [token]);

  const fetchUsers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUsers(data.users || []);
      }
    } catch (e) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const fetchPermissionsTemplate = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/permissions-template`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPermissionsTemplate(data);
      }
    } catch (e) {
      console.error('Failed to fetch permissions template');
    }
  };

  const handleOpenModal = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        name: user.name,
        email: user.email,
        password: '',
        role: user.role,
        permissions: user.permissions || {}
      });
    } else {
      setEditingUser(null);
      setFormData({
        name: '',
        email: '',
        password: '',
        role: 'sub_admin',
        permissions: getDefaultPermissions()
      });
    }
    setShowModal(true);
  };

  const getDefaultPermissions = () => {
    const perms = {};
    permissionsTemplate?.sections?.forEach(section => {
      perms[section.key] = section.key === 'user_management' ? 'none' : 'view';
    });
    return perms;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        role: formData.role,
        permissions: formData.role === 'super_admin' ? null : formData.permissions
      };
      
      if (formData.password) {
        payload.password = formData.password;
      }

      let res;
      if (editingUser) {
        res = await fetch(`${API_URL}/api/admin/users/${editingUser.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(payload)
        });
      } else {
        if (!formData.password) {
          toast.error('Password is required for new users');
          return;
        }
        res = await fetch(`${API_URL}/api/admin/users`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(payload)
        });
      }

      const data = await res.json();
      if (res.ok) {
        toast.success(editingUser ? 'User updated successfully' : 'User created successfully');
        setShowModal(false);
        fetchUsers();
      } else {
        toast.error(data.detail || 'Operation failed');
      }
    } catch (e) {
      toast.error('Failed to save user');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('User deleted successfully');
        fetchUsers();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to delete user');
      }
    } catch (e) {
      toast.error('Failed to delete user');
    }
  };

  const handleToggleActive = async (user) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${user.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ is_active: !user.is_active })
      });
      
      if (res.ok) {
        toast.success(`User ${user.is_active ? 'deactivated' : 'activated'} successfully`);
        fetchUsers();
      }
    } catch (e) {
      toast.error('Failed to update user status');
    }
  };

  const handlePermissionChange = (section, value) => {
    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [section]: value
      }
    }));
  };

  if (!isSuperAdmin()) {
    return (
      <div className="p-6">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 text-red-600">
              <AlertCircle size={24} />
              <div>
                <h3 className="font-semibold">Access Denied</h3>
                <p className="text-sm">Only Super Admins can manage users.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">User Management</h1>
          <p className="text-text-secondary">Manage admin users and their permissions</p>
        </div>
        <Button onClick={() => handleOpenModal()} className="bg-tech-blue hover:bg-tech-blue/90">
          <Plus size={18} className="mr-2" />
          Add Admin User
        </Button>
      </div>

      {/* Users List */}
      <Card className="border-border-line">
        <CardHeader>
          <CardTitle className="flex items-center">
            <Users className="mr-2 text-tech-blue" size={20} />
            Admin Users ({users.length})
          </CardTitle>
          <CardDescription>All users with access to the admin panel</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-text-secondary">Loading users...</div>
          ) : users.length === 0 ? (
            <div className="text-center py-8 text-text-secondary">No users found</div>
          ) : (
            <div className="space-y-3">
              {users.map(user => (
                <div 
                  key={user.id} 
                  className={`flex items-center justify-between p-4 rounded-lg border ${
                    user.is_active ? 'bg-white border-border-line' : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      user.role === 'super_admin' ? 'bg-heritage-orange/10' : 'bg-tech-blue/10'
                    }`}>
                      {user.role === 'super_admin' ? (
                        <ShieldCheck className="text-heritage-orange" size={24} />
                      ) : (
                        <Shield className="text-tech-blue" size={24} />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-text-primary">{user.name}</h3>
                        <span className={`px-2 py-0.5 text-xs rounded-full ${
                          user.role === 'super_admin' 
                            ? 'bg-heritage-orange/10 text-heritage-orange' 
                            : 'bg-tech-blue/10 text-tech-blue'
                        }`}>
                          {user.role === 'super_admin' ? 'Super Admin' : 'Sub Admin'}
                        </span>
                        {!user.is_active && (
                          <span className="px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-600">
                            Inactive
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-text-secondary">{user.email}</p>
                      {user.last_login && (
                        <p className="text-xs text-text-secondary mt-1">
                          Last login: {new Date(user.last_login).toLocaleString()}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {user.id !== adminUser?.id && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleToggleActive(user)}
                          className={user.is_active ? 'text-amber-600 border-amber-200' : 'text-green-600 border-green-200'}
                        >
                          {user.is_active ? <EyeOff size={16} /> : <Eye size={16} />}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleOpenModal(user)}
                        >
                          <Edit size={16} />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(user.id)}
                          className="text-red-600 border-red-200 hover:bg-red-50"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </>
                    )}
                    {user.id === adminUser?.id && (
                      <span className="text-xs text-text-secondary px-2 py-1 bg-gray-100 rounded">
                        You
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border-line sticky top-0 bg-white">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-text-primary">
                  {editingUser ? 'Edit Admin User' : 'Add Admin User'}
                </h2>
                <Button variant="ghost" size="sm" onClick={() => setShowModal(false)}>
                  <X size={20} />
                </Button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">
                    <User size={14} className="inline mr-1" />
                    Full Name
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-3 py-2 border border-border-line rounded-lg focus:ring-2 focus:ring-tech-blue focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">
                    <Mail size={14} className="inline mr-1" />
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-3 py-2 border border-border-line rounded-lg focus:ring-2 focus:ring-tech-blue focus:border-transparent"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">
                    <Lock size={14} className="inline mr-1" />
                    Password {editingUser && '(leave blank to keep current)'}
                  </label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className="w-full px-3 py-2 border border-border-line rounded-lg focus:ring-2 focus:ring-tech-blue focus:border-transparent"
                    required={!editingUser}
                    minLength={8}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">
                    <Shield size={14} className="inline mr-1" />
                    Role
                  </label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({...formData, role: e.target.value})}
                    className="w-full px-3 py-2 border border-border-line rounded-lg focus:ring-2 focus:ring-tech-blue focus:border-transparent"
                  >
                    <option value="sub_admin">Sub Admin</option>
                    <option value="super_admin">Super Admin</option>
                  </select>
                </div>
              </div>

              {/* Permissions */}
              {formData.role === 'sub_admin' && permissionsTemplate && (
                <div>
                  <h3 className="text-lg font-semibold text-text-primary mb-3">Permissions</h3>
                  <div className="border border-border-line rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="text-left px-4 py-2 text-sm font-medium text-text-secondary">Section</th>
                          <th className="text-center px-4 py-2 text-sm font-medium text-text-secondary">No Access</th>
                          <th className="text-center px-4 py-2 text-sm font-medium text-text-secondary">View Only</th>
                          <th className="text-center px-4 py-2 text-sm font-medium text-text-secondary">Full Access</th>
                        </tr>
                      </thead>
                      <tbody>
                        {permissionsTemplate.sections
                          .filter(section => section.key !== 'user_management')
                          .map(section => (
                          <tr key={section.key} className="border-t border-border-line">
                            <td className="px-4 py-3">
                              <div className="font-medium text-text-primary text-sm">{section.label}</div>
                              <div className="text-xs text-text-secondary">{section.description}</div>
                            </td>
                            <td className="text-center px-4 py-3">
                              <input
                                type="radio"
                                name={`perm_${section.key}`}
                                checked={formData.permissions[section.key] === 'none'}
                                onChange={() => handlePermissionChange(section.key, 'none')}
                                className="w-4 h-4 text-red-600"
                              />
                            </td>
                            <td className="text-center px-4 py-3">
                              <input
                                type="radio"
                                name={`perm_${section.key}`}
                                checked={formData.permissions[section.key] === 'view'}
                                onChange={() => handlePermissionChange(section.key, 'view')}
                                className="w-4 h-4 text-amber-600"
                              />
                            </td>
                            <td className="text-center px-4 py-3">
                              <input
                                type="radio"
                                name={`perm_${section.key}`}
                                checked={formData.permissions[section.key] === 'edit'}
                                onChange={() => handlePermissionChange(section.key, 'edit')}
                                className="w-4 h-4 text-green-600"
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-text-secondary mt-2">
                    * User Management is always restricted to Super Admins only
                  </p>
                </div>
              )}

              {formData.role === 'super_admin' && (
                <div className="bg-heritage-orange/10 border border-heritage-orange/20 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-heritage-orange">
                    <ShieldCheck size={20} />
                    <span className="font-medium">Super Admin</span>
                  </div>
                  <p className="text-sm text-text-secondary mt-1">
                    Super Admins have full access to all sections including User Management.
                  </p>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-border-line">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-tech-blue hover:bg-tech-blue/90">
                  <Save size={16} className="mr-2" />
                  {editingUser ? 'Update User' : 'Create User'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsersPage;
